/**
 * Confine a stylesheet to a single container element.
 *
 * the editor's printable-area preview applies the PDF export template's stylesheet so
 * authors see the export's real typography. That stylesheet is written for a standalone print
 * document, so it styles bare `table`, `tr`, `img` and `button` - injecting it as-is would restyle
 * the whole app. Prefixing every selector with the preview container makes it safe.
 *
 * `@media print` blocks are inlined rather than dropped: the export renders with
 * `page.emulateMediaType('print')`, so those rules are part of what the author actually gets.
 * `@page` is dropped - it has no meaning outside real pagination.
 */

// Selectors that mean "the document" in a standalone page. Inside the preview the container IS the
// document, so they collapse onto the scope rather than being prefixed by it.
const DOCUMENT_SELECTORS = ['html', 'body', ':root'];

const stripComments = (css) => css.replace(/\/\*[\s\S]*?\*\//g, '');

const scopeSelectorList = (selectorList, scope) =>
  selectorList
    .split(',')
    .map((raw) => {
      const selector = raw.trim();

      if (selector.length === 0) {
        return null;
      }
      if (DOCUMENT_SELECTORS.includes(selector)) {
        return scope;
      }
      // Already inside the scope - leave it alone rather than double-prefixing.
      if (selector.startsWith(scope)) {
        return selector;
      }
      return `${scope} ${selector}`;
    })
    .filter(Boolean)
    .join(', ');

/**
 * Split a stylesheet into top-level `{ prelude, body }` chunks, respecting nested braces so
 * at-rules such as `@media` come back whole.
 */
const splitRules = (css) => {
  const rules = [];
  let depth = 0;
  let start = 0;
  let preludeEnd = -1;

  for (let i = 0; i < css.length; i += 1) {
    if (css[i] === '{') {
      if (depth === 0) {
        preludeEnd = i;
      }
      depth += 1;
    } else if (css[i] === '}') {
      depth -= 1;
      if (depth === 0) {
        rules.push({
          prelude: css.slice(start, preludeEnd).trim(),
          body: css.slice(preludeEnd + 1, i)
        });
        start = i + 1;
      }
    }
  }

  return rules;
};

/**
 * @param {string} css - the stylesheet to confine.
 * @param {string} scope - a selector for the container, e.g. `.print-preview-content`.
 * @returns {string} the same rules, each restricted to descendants of `scope`.
 */
export const scopeCss = (css, scope) => {
  if (typeof css !== 'string' || css.length === 0 || !scope) {
    return '';
  }

  return splitRules(stripComments(css))
    .map(({ prelude, body }) => {
      if (prelude.startsWith('@')) {
        // Print rules describe the exported document, so they belong in the preview.
        if (/^@media\s+print/i.test(prelude)) {
          return scopeCss(body, scope);
        }
        // Page geometry cannot be expressed on screen; the preview draws its own page rules.
        if (/^@page/i.test(prelude)) {
          return '';
        }
        // Anything else (@font-face, @media screen, @supports) keeps its wrapper, contents scoped.
        return `${prelude} { ${scopeCss(body, scope)} }`;
      }

      const scoped = scopeSelectorList(prelude, scope);

      return scoped.length > 0 ? `${scoped} {${body}}` : '';
    })
    .filter((rule) => rule.trim().length > 0)
    .join('\n');
};

export default scopeCss;
